// =====================================================================
// SMART ACCOUNTANT TOOLS
// =====================================================================
//   1. Amount-in-words tooltip (AR tafqit + EN) on Currency/Float fields
//   2. Quick copy: Alt+Click a money field/cell → raw number to clipboard
//   3. Selection bar: count of checked list rows + strict column sums in
//      report DataTables (never shows a wrong total — falls back to count)
//
// Every visible element is body-appended + position:fixed (proven safe
// pattern) so nothing can be trapped behind Frappe stacking contexts.
// Gated by uxPrefs.accountant. Fully defensive — never throws into Frappe.
// =====================================================================

import { uxPrefs } from "./ux_prefs.js";

// ---------- number → words ------------------------------------------

const EN_ONES = ["", "one", "two", "three", "four", "five", "six", "seven",
	"eight", "nine", "ten", "eleven", "twelve", "thirteen", "fourteen",
	"fifteen", "sixteen", "seventeen", "eighteen", "nineteen"];
const EN_TENS = ["", "", "twenty", "thirty", "forty", "fifty", "sixty",
	"seventy", "eighty", "ninety"];
const EN_SCALE = ["", "thousand", "million", "billion", "trillion"];

function enHundreds(n) {
	let s = "";
	if (n >= 100) {
		s += EN_ONES[Math.floor(n / 100)] + " hundred";
		n %= 100;
		if (n) s += " ";
	}
	if (n >= 20) {
		s += EN_TENS[Math.floor(n / 10)];
		if (n % 10) s += "-" + EN_ONES[n % 10];
	} else if (n > 0) {
		s += EN_ONES[n];
	}
	return s;
}

function enWords(num) {
	num = Math.floor(Math.abs(num));
	if (num === 0) return "zero";
	const groups = [];
	while (num > 0) { groups.push(num % 1000); num = Math.floor(num / 1000); }
	let out = [];
	for (let i = groups.length - 1; i >= 0; i--) {
		if (groups[i] === 0) continue;
		out.push(enHundreds(groups[i]) + (EN_SCALE[i] ? " " + EN_SCALE[i] : ""));
	}
	return out.join(" ").replace(/\s+/g, " ").trim();
}

// Arabic tafqit — handles ones/tens/hundreds + thousand/million/billion
// with correct و connectors and dual forms (ألفان/مليونان…).
const AR_ONES = ["", "واحد", "اثنان", "ثلاثة", "أربعة", "خمسة", "ستة",
	"سبعة", "ثمانية", "تسعة", "عشرة", "أحد عشر", "اثنا عشر", "ثلاثة عشر",
	"أربعة عشر", "خمسة عشر", "ستة عشر", "سبعة عشر", "ثمانية عشر",
	"تسعة عشر"];
const AR_TENS = ["", "", "عشرون", "ثلاثون", "أربعون", "خمسون", "ستون",
	"سبعون", "ثمانون", "تسعون"];
const AR_HUND = ["", "مئة", "مئتان", "ثلاثمئة", "أربعمئة", "خمسمئة",
	"ستمئة", "سبعمئة", "ثمانمئة", "تسعمئة"];
const AR_SCALE = [
	["", "", ""],
	["ألف", "ألفان", "آلاف"],
	["مليون", "مليونان", "ملايين"],
	["مليار", "ملياران", "مليارات"],
	["تريليون", "تريليونان", "تريليونات"],
];

function arBelow1000(n) {
	const parts = [];
	if (n >= 100) { parts.push(AR_HUND[Math.floor(n / 100)]); n %= 100; }
	if (n >= 20) {
		const t = AR_TENS[Math.floor(n / 10)];
		const o = n % 10;
		parts.push(o ? AR_ONES[o] + " و" + t : t);
	} else if (n > 0) {
		parts.push(AR_ONES[n]);
	}
	return parts.join(" و");
}

function arScale(count, idx) {
	const forms = AR_SCALE[idx];
	if (!idx) return arBelow1000(count);
	if (count === 1) return forms[0];
	if (count === 2) return forms[1];
	if (count >= 3 && count <= 10) return arBelow1000(count) + " " + forms[2];
	return arBelow1000(count) + " " + forms[0];
}

function arWords(num) {
	num = Math.floor(Math.abs(num));
	if (num === 0) return "صفر";
	const groups = [];
	while (num > 0) { groups.push(num % 1000); num = Math.floor(num / 1000); }
	const out = [];
	for (let i = groups.length - 1; i >= 0; i--) {
		if (groups[i] === 0) continue;
		out.push(arScale(groups[i], i));
	}
	return out.join(" و");
}

function inWords(value, lang) {
	const neg = value < 0;
	const abs = Math.abs(Number(value) || 0);
	const intPart = Math.floor(abs);
	const frac = Math.round((abs - intPart) * 100);
	if (lang === "ar") {
		let s = arWords(intPart);
		if (frac > 0) s += " فاصلة " + arWords(frac) + "/١٠٠";
		return (neg ? "سالب " : "") + s;
	}
	let s = enWords(intPart);
	if (frac > 0) s += " and " + String(frac).padStart(2, "0") + "/100";
	return (neg ? "minus " : "") + s.charAt(0).toUpperCase() + s.slice(1);
}

// ---------- numeric parsing (strict) --------------------------------

const AR_DIGITS = { "٠": "0", "١": "1", "٢": "2", "٣": "3", "٤": "4",
	"٥": "5", "٦": "6", "٧": "7", "٨": "8", "٩": "9", "٫": "." };

function strictNumber(text) {
	if (text == null) return null;
	let t = String(text).trim();
	t = t.replace(/[٠-٩٫]/g, (d) => AR_DIGITS[d] ?? d);
	// keep digits, separators, sign, parentheses (accounting negatives)
	const paren = /^\(.*\)$/.test(t);
	t = t.replace(/[^\d.,\-]/g, "");
	if (!t) return null;
	// 1.234,56 (eu) vs 1,234.56 (us) — decide by last separator
	const lastDot = t.lastIndexOf(".");
	const lastCom = t.lastIndexOf(",");
	if (lastCom > lastDot) {
		t = t.replace(/\./g, "").replace(",", ".");
	} else {
		t = t.replace(/,/g, "");
	}
	const n = parseFloat(t);
	if (!isFinite(n)) return null;
	return paren ? -n : n;
}

function fieldValue(el) {
	if (!el) return null;
	const raw = el.value !== undefined ? el.value : el.textContent;
	return strictNumber(raw);
}

// ---------- tooltip --------------------------------------------------

let tip = null;
function ensureTip() {
	if (tip) return tip;
	tip = document.createElement("div");
	tip.className = "e16-amt-tip";
	document.body.appendChild(tip);
	return tip;
}

function lang() {
	return document.documentElement.getAttribute("lang") === "ar" ? "ar" : "en";
}

// Never interfere while a grid row editor is open / form is busy.
function busy() {
	try {
		if (document.querySelector(".grid-row-open")) return true;
		if (window.frappe && frappe.dom && frappe.dom.freeze_count > 0) return true;
	} catch (e) {}
	return false;
}

function showTip(el) {
	if (!uxPrefs.get().accountant) return;
	if (busy()) return;
	// HARD grid guard — never engage anywhere inside a Child Table.
	try {
		if (el.closest && el.closest(".form-grid, .grid-row, .form-in-grid, .form-grid-container")) return;
	} catch (e) { return; }
	const v = fieldValue(el);
	if (v == null || !isFinite(v)) return;
	const lg = lang();
	const cap = lg === "ar" ? "بالكلمات" : "In words";
	const words = inWords(v, lg);
	const t = ensureTip();
	t.innerHTML =
		'<span class="e16-amt-tip__cap">' + cap + "</span><b>" +
		frappe_escape(words) + "</b>";
	const r = el.getBoundingClientRect();
	t.style.left = Math.max(8, Math.round(r.left)) + "px";
	t.style.top = Math.round(r.bottom + 8) + "px";
	t.classList.add("is-on");
	// keep inside viewport horizontally
	requestAnimationFrame(() => {
		const tr = t.getBoundingClientRect();
		if (tr.right > window.innerWidth - 8) {
			t.style.left = Math.round(window.innerWidth - tr.width - 8) + "px";
		}
		if (tr.bottom > window.innerHeight - 8) {
			t.style.top = Math.round(r.top - tr.height - 8) + "px";
		}
	});
}

function hideTip() {
	if (tip) tip.classList.remove("is-on");
}

function frappe_escape(s) {
	return String(s).replace(/[&<>"']/g, (c) => ({
		"&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
	}[c]));
}

// NOTE: NO `.grid-static-col` selectors — the amount-in-words tooltip
// must NEVER engage inside the Child Table grid (zero grid JS).
const MONEY_SEL =
	'.frappe-control[data-fieldtype="Currency"] input,' +
	'.frappe-control[data-fieldtype="Float"] input,' +
	'.frappe-control[data-fieldtype="Int"] input,' +
	'.frappe-control[data-fieldtype="Percent"] input';

// ---------- quick copy ----------------------------------------------

function copy(text) {
	const done = () => toast(lang() === "ar" ? "تم النسخ" : "Copied");
	try {
		if (navigator.clipboard && navigator.clipboard.writeText) {
			navigator.clipboard.writeText(text).then(done).catch(() => fallbackCopy(text, done));
		} else {
			fallbackCopy(text, done);
		}
	} catch (e) { fallbackCopy(text, done); }
}

function fallbackCopy(text, done) {
	try {
		const ta = document.createElement("textarea");
		ta.value = text;
		ta.style.position = "fixed";
		ta.style.opacity = "0";
		document.body.appendChild(ta);
		ta.select();
		document.execCommand("copy");
		document.body.removeChild(ta);
		done && done();
	} catch (e) {}
}

function toast(msg) {
	try {
		frappe.show_alert({ message: msg, indicator: "green" }, 3);
	} catch (e) {}
}

// ---------- selection bar -------------------------------------------

let selBar = null;
function ensureSelBar() {
	if (selBar) return selBar;
	selBar = document.createElement("div");
	selBar.className = "e16-selbar";
	document.body.appendChild(selBar);
	return selBar;
}

function updateSelBar() {
	if (!uxPrefs.get().accountant) {
		if (selBar) selBar.classList.remove("is-on");
		return;
	}
	let count = 0;
	let sums = null;
	try {
		// Report DataTable selection (strict, with total fallback)
		const dt = document.querySelector(".datatable");
		if (dt) {
			const checked = dt.querySelectorAll(
				'.dt-cell--col-0 input:checked, .dt-row--highlight'
			);
			// Prefer checkbox rows
			const rows = dt.querySelectorAll(".dt-row");
			const sel = [];
			rows.forEach((row) => {
				const cb = row.querySelector('input[type="checkbox"]:checked');
				if (cb) sel.push(row);
			});
			count = sel.length || checked.length;
			if (sel.length >= 2) {
				const colSums = {};
				let ok = true;
				const headers = dt.querySelectorAll(".dt-row-header .dt-cell");
				sel.forEach((row) => {
					row.querySelectorAll(".dt-cell").forEach((cell) => {
						const txt = (cell.textContent || "").trim();
						if (!txt || !/[\d٠-٩]/.test(txt)) return;
						if (/[A-Za-zء-ي]{3,}/.test(txt.replace(/[٠-٩]/g, ""))) return;
						const n = strictNumber(txt);
						if (n == null) return;
						const ci = cell.className.match(/dt-cell--col-(\d+)/);
						const key = ci ? ci[1] : "?";
						colSums[key] = (colSums[key] || 0) + n;
					});
				});
				if (ok && Object.keys(colSums).length) {
					sums = { colSums, headers };
				}
			}
		}
		if (!count) {
			// Plain list view → count only (always reliable)
			count = document.querySelectorAll(
				".list-row-checkbox:checked, .list-row-container .list-row.selected"
			).length;
		}
	} catch (e) {
		// never break the page
	}

	const bar = ensureSelBar();
	if (count < 2) { bar.classList.remove("is-on"); return; }
	const lg = lang();
	let html =
		(lg === "ar" ? "محدد: <b>" : "Selected: <b>") + count + "</b>";
	if (sums && sums.colSums) {
		const keys = Object.keys(sums.colSums).slice(0, 3);
		const fmt = (n) => {
			try {
				return n.toLocaleString(lg === "ar" ? "ar-EG" : "en-US", {
					maximumFractionDigits: 2,
				});
			} catch (e) { return String(Math.round(n * 100) / 100); }
		};
		keys.forEach((k) => {
			let label = "Σ";
			try {
				const h = sums.headers[Number(k)];
				if (h) label = (h.textContent || "Σ").trim().slice(0, 16);
			} catch (e) {}
			html += ' &nbsp;·&nbsp; ' + frappe_escape(label) +
				": <b>" + fmt(sums.colSums[k]) + "</b>";
		});
	}
	bar.innerHTML = html;
	bar.classList.add("is-on");
}

// ---------- mount ----------------------------------------------------

let mounted = false;

export const accountantTools = {
	mount() {
		if (mounted) return;
		mounted = true;

		// SAFE event surface only:
		//   • focusin  → fires only when the user focuses a money field
		//                (intentional, rare — no mouseover storm).
		//   • Alt+Click → opt-in quick copy.
		// No mouseover, no selection observers, no document MutationObserver
		// → cannot interfere with Frappe's grid rendering.
		document.addEventListener(
			"focusin",
			(e) => {
				try {
					const el = e.target.closest && e.target.closest(MONEY_SEL);
					if (el) showTip(el);
				} catch (er) {}
			},
			true
		);
		document.addEventListener("focusout", hideTip, true);
		window.addEventListener("scroll", hideTip, { passive: true, capture: true });

		// Alt+Click quick copy (only when Alt held → never accidental)
		document.addEventListener(
			"click",
			(e) => {
				try {
					if (!e.altKey) return;
					if (!uxPrefs.get().accountant) return;
					if (busy()) return;
					const el = e.target.closest && e.target.closest(MONEY_SEL);
					if (!el) return;
					const v = fieldValue(el);
					if (v == null) return;
					e.preventDefault();
					e.stopPropagation();
					copy(String(v));
				} catch (er) {}
			},
			true
		);

		document.addEventListener("e16:ux-applied", hideTip);
	},
};
